import React from 'react';
import styled from 'styled-components';

import { viewerZIndices } from '../../../../viewer/viewer-z-indices';
import { reset } from '../../../utils/styled-reset';
import { Icon } from '../../icon/icon';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  collapsed: boolean;
  width: number;
};

const CollapsableContainer = (props: Props) => {
  const { children, collapsed, width, ...rest } = props;
  return (
    <Component collapsed={collapsed} width={width} {...rest}>
      <OverflowWrapper>
        <FixedWidthContainer width={width}>{children}</FixedWidthContainer>
      </OverflowWrapper>
    </Component>
  );
};

const AssetMenuThumb = (props: {
  collapsed: boolean;
  setCollapsed: (state: (oldValue: boolean) => boolean) => void;
}) => {
  return (
    <ToggleThumb onClick={() => props.setCollapsed((oldValue) => !oldValue)}>
      <Icon
        color="white"
        fixedWidth={true}
        icon={['fas', props.collapsed ? 'chevron-right' : 'chevron-left']}
      />
    </ToggleThumb>
  );
};

const Component = styled.div<{ collapsed: boolean; width: number }>`
  ${reset}

  position: relative;
  width: ${(props) => (props.collapsed ? '0' : `${props.width}px`)};
  transition: width 0.5s ease-in-out;
  z-index: ${viewerZIndices.menu};

  // overflow: hidden makes this grid item's min-content size contribution 0,
  // preventing the 1fr row from expanding beyond available space.
  overflow: hidden;
`;

const OverflowWrapper = styled.div`
  // Use absolute positioning instead of height: 100% to avoid percentage height
  // resolution issues with CSS Grid fr tracks.
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const FixedWidthContainer = styled.div<{ width: number }>`
  height: 100%;
  width: ${(props) => props.width}px;
`;

const ToggleThumb = styled.div`
  pointer-events: auto;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(0, -50%);

  padding: 3em 0.1em;
  font-size: 0.8em;
  background-color: #5f6a71;
  cursor: pointer;
  border-top-right-radius: 0.5em;
  border-bottom-right-radius: 0.5em;

  // Border color same as background color in Skyview.
  border-top: 1px solid #ccddf0;
  border-right: 1px solid #ccddf0;
  border-bottom: 1px solid #ccddf0;

  &:hover {
    background-color: #4b555a;
  }
`;

export { AssetMenuThumb, CollapsableContainer, Component as CollapsableContainerStyled };
